import React, {useCallback, useEffect, useMemo, useState} from "react";
import "./App.css";
import {Route, Switch, useHistory} from "react-router-dom";
import Routes from "../routing/Routes";
import {
    MainLoader,
    setAlert,
    NavbarMultiOrgs,
    ADTE_LOGO_SMALL_URL,
    darkTheme,
    theme,
    AuthActions,
    AuthGState,
    Footer,
    getAllOrgsIdsForUserByServiceName,
    apiClient,
    useFavoriteTheme,
    CssBaseline,
    ThemeProvider,
    Logout
} from "@leap4/frontend";
import {useLocation} from "react-router-dom";
import {ConnectedProps, useDispatch} from "react-redux";
import {connect} from "react-redux";
import {
    BACKEND_SERVICE_NAME,
    logoutUrl,
    meMultiUrl,
    requiredAuthRoles,
    userViewsBaseUrl
} from "../../config/generals";
import {RootState} from "../../interfaces/root-state.interface";
import {urlService} from "../routing/urlsService";
import {QueryClient, QueryClientProvider} from "react-query";
import {UserViewsDialog} from "./UserViewsDialog";
import {setIsLoading} from "../../store/actions/dateRange.actions";
import {emptyUserView, UserViewsContext} from "../../contexts/userViewsContext";

// Styles (replaces makeStyles which was removed in MUI v7)
const NAVBAR_HEIGHT = 50;
const styles = {
    root: { display: "flex", flexDirection: "column" as const, minHeight: "100vh" },
    toolbar: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    content: { flexGrow: 1, width: "100%", margin: "auto", padding: "0 1px" },
};


const mapStateToProps = (state: RootState) => ({
    auth: state.auth,
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;


interface IProps extends PropsFromRedux {
    history: History,
    auth: AuthGState
}

const queryClient = new QueryClient();

const App = ({auth, setAlert}: IProps) => {
    const history = useHistory();
    const [selectedTheme, setSelectedTheme] = useFavoriteTheme()
    const location = useLocation();
    const dispatch = useDispatch();
    const [bIsSidebarOpen, SetSideBarOpenState] = useState(false);
    const [isLoading, setIsLoading] = useState(true)
    const [currentUserId, setCurrentUserId] = useState('')
    const [userViews, setUserViews] = useState({
        userViewsPresets: [emptyUserView],
        activePreset: emptyUserView
    })
    const [isViewsPopupOpen, setViewsPopupOpen] = useState(false)
    const nSidebarWidth = 240;

    const userOrganizations = useMemo(() => {
        if (!auth.user) return [];
        return getAllOrgsIdsForUserByServiceName(auth.user, auth.user.organizations as string[], requiredAuthRoles, BACKEND_SERVICE_NAME);
    }, [auth.user?._id]);

    const handleThemeChange = useCallback((checked) => {
        const themeToShow = checked ? darkTheme : theme;
        setSelectedTheme(themeToShow)
    }, [setSelectedTheme]);

    const handleDrawerClose = useCallback(() => SetSideBarOpenState(false), []);
    const handleDrawerOpen = useCallback(() => SetSideBarOpenState(true), []);
    const handleOpenViewsPopup = useCallback(() => setViewsPopupOpen(true), []);

    const navbarAuthProp = useMemo(() => ({isAuthenticated: true}), []);
    const navbarUiProp = useMemo(() => ({nSidebarWidth, nNavbarHeight: NAVBAR_HEIGHT}), [nSidebarWidth]);

    const logoutProp = useMemo(() => ({
        text: "Logout",
        action: async () => {
            sessionStorage.clear();
            await AuthActions.logout(dispatch, history, logoutUrl)
        },
        icon: <Logout/>
    }), [dispatch, history]);
    useEffect(() => {
        (async () => {
                try {
                    await AuthActions.loadUser(dispatch, BACKEND_SERVICE_NAME, meMultiUrl)
                } finally {
                    setIsLoading(false)
                }
            }
        )();
    }, []);


    useEffect(() => {
        const fetchUserViews = async () =>{
            if(currentUserId) {
                const userViewsPresetsFromServer = (await apiClient.get(`${userViewsBaseUrl}/${currentUserId}`)).data
                const activePreset = userViewsPresetsFromServer.find(preset => preset.isActivePreset)
                if(!activePreset) setUserViews({userViewsPresets: userViewsPresetsFromServer, activePreset: userViewsPresetsFromServer[0]})
                else setUserViews({userViewsPresets: userViewsPresetsFromServer, activePreset} )
            }
        }
        fetchUserViews()
    }, [currentUserId])

    useEffect(()=>{
        if(auth.user){
            if(auth.user._id !== currentUserId) setCurrentUserId(auth.user._id)
        }
    },[auth.user?._id])

    const navbarLinks = useMemo(() => [
        {text: "Home", link: urlService.home()},
        {text: "Reports", link: urlService.reports()},
        {text: "Data-Points", link: urlService.dataPoints()},
        {text: "Ad-Servers", link: urlService.adServers()},
        {
            text: "Partners", link: [
                {
                    text: 'Partners', link: [
                        {text: 'Demand', link: urlService.partnersDemand()},
                        {text: 'Supply', link: urlService.partnersSupply()},
                        {text: 'Create New Partner', link: urlService.createPartner()},
                    ]
                },
                {
                    text: 'Aliases', link: [
                        {text: 'Demand', link: urlService.partnersAliasDemand()},
                        {text: 'Supply', link: urlService.partnersAliasSupply()},
                        {text: 'Create New Alias', link: urlService.createAlias()}
                    ]
                },
                {
                    text: 'Assign', link: [
                        {text: 'Demand', link: urlService.partnersAssignDemand()},
                        {text: 'Supply', link: urlService.partnersAssignSupply()},
                    ]
                },
                {
                    text: 'Overview', link: [
                        {text: 'Demand', link: urlService.partnersOverviewDemand()},
                        {text: 'Supply', link: urlService.partnersOverviewSupply()},
                    ]
                },
            ],
        },
        {
            text: "Tags", link: [
                {text: 'Publisher Tags', link: urlService.publisherTags()},
                {text: 'Ad Server Tags', link: urlService.adServerTags()}]
        },
        {text: "Discrepancy", link: urlService.publisherDataPoints()},
        {text: "Price-Rules", link: urlService.priceRules()},
        {
            text: "Alerts", link: [
                {text: 'Alerts', link: urlService.alerts()},
                {text: 'Groups', link: urlService.groups()}]
        }

    ], []);

    if (isLoading) {

        return <MainLoader/>
    }


    return (
        <>
            <ThemeProvider theme={selectedTheme}>
                <QueryClientProvider client={queryClient}>
                    <UserViewsContext.Provider value={{userViews, setUserViews}}>
                    <div style={styles.root}>
                        <CssBaseline/>

                        {auth?.user && <NavbarMultiOrgs
                            bIsSidebarOpen={bIsSidebarOpen}
                            handleDrawerClose={handleDrawerClose}
                            handleDrawerOpen={handleDrawerOpen}
                            auth={navbarAuthProp}
                            ui={navbarUiProp}
                            location={location.pathname}
                            links={navbarLinks}
                            orgsWithPermssions={userOrganizations}
                            updateCheckedToFather={handleThemeChange}
                            title={''}
                            logoImageUrl={ADTE_LOGO_SMALL_URL}
                            logout={logoutProp}
                            handleOpenViewsPopup={handleOpenViewsPopup}
                        />}


                        <main style={styles.content}>
                            {/*<div style={{width: "100vw", height: 100, backgroundColor: 'violet'}}  />*/}
                            <div style={styles.toolbar}/>
                            <Switch>
                                <Route component={Routes}/>{" "}
                            </Switch>
                            <UserViewsDialog isViewsPopupOpen={isViewsPopupOpen} setViewsPopupOpen={setViewsPopupOpen}/>
                        </main>
                        <Footer text={'X-Dash'}
                                logoUrl={ADTE_LOGO_SMALL_URL}/>
                    </div>
            </UserViewsContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>

        </>
    );
};


export default connector(App);
